import React, {
    Component,
    MouseEvent
} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import _ from 'lodash';
import parse from 'html-react-parser';
import memoize from 'memoize-one';

import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';
import {
    TreeExpandedEvent,
    TreeExpandedKeysType,
    TreeSelectionEvent
} from 'primereact/tree';

import './style.scss';

import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import Item, {ItemAttributesForCreation} from '~/shared/src/ci/Item';
import Reference, {ReferenceAttributesForCreation} from '~/shared/src/ci/Reference';
import {getXmlForContext} from '~/shared/src/utilities/xmlExport';
import {getMetaModelXmlForContext} from '~/shared/src/utilities/xmlMetaModelExport';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import User from '~/shared/src/model/User';
import {
    rawModeSchema,
    showAdditionalRawNamesSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {SaveDataResult} from '~/shared/src/utilities/modules/data';
import metaModel from '~/shared/src/utilities/metaModel';
import messageUtils from '~/shared/src/utilities/messageUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    GetViewKeyAndRootItemIdByNameAndDisplayAttributeResult,
    RequestConfig
} from '~/shared/src/utilities/modules/form';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';

import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import DetailHeaderbar from '~/frontend/src/components/base/ItemFinder/detail/Headerbar';
import Form, {
    Form as FormClass,
    componentProps as formComponentProps,
    ComponentProps as FormComponentProps
} from '~/frontend/src/components/base/Form';
import ItemTree, {
    ItemTree as ItemTreeClass,
    AllProps as ItemTreeProps,
    APIParams
} from '~/frontend/src/components/base/shared/tree/ItemTree';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Overview, {
    componentProps as overviewComponentProps,
    ComponentProps as OverviewComponentProps
} from '~/frontend/src/components/base/Overview';
import ResizeableLeftMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableLeftMenuPanel';
import Toolbar from '~/frontend/src/components/base/TreeView/Toolbar';
import TreeViewHeaderbar from '~/frontend/src/components/base/TreeView/Headerbar';

import keyboardUtils from '~/frontend/src/utilities/keyboardUtils';
import TreeUtils from '~/frontend/src/utilities/TreeUtils';
import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    ItemTitleConfig,
    ItemTypeIconConfig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';
import {
    getStatusColorStyleString,
    nl2br
} from '~/frontend/src/utilities/renderHelpers';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {PanelPropsByPanelType} from '~/frontend/src/components/base/page/PageTabPanel/definitions';
import {
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';
import {
    getPanelElusiveData,
    getPanelProps,
    getSubPanelErrors
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

import {
    AppDispatch,
    store,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import loadData, {
    LoadData
} from '~/frontend/src/actions/data/loadData';
import setParam, {
    SetParamAction
} from '~/frontend/src/actions/panel/common/setParam';
import setParams, {
    SetParamsAction
} from '~/frontend/src/actions/panel/common/setParams';
import {
    clearElusiveData,
    ClearElusiveData,
    setElusiveData,
    SetElusiveData,
    clearElusiveDataForSubpanels,
    ClearElusiveDataForSubpanels
} from '~/frontend/src/actions/elusiveData';
import {
    MsgParams,
    setMessage,
    SetMessage,
    unsetMessage,
    UnsetMessage
} from '~/frontend/src/actions/intercom';
import setPanelDirty, {
    SetPanelDirty
} from '~/frontend/src/actions/panelManager/setPanelDirty';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import saveData, {
    SaveData
} from '~/frontend/src/actions/panel/treeView/saveData';
import closePanel, {
    ClosePanel
} from '~/frontend/src/actions/panelManager/closePanel';
import openPanel, {
    OpenPanel
} from '~/frontend/src/actions/panelManager/openPanel';
import setPanelPath, {
    SetPanelPath
} from '~/frontend/src/actions/panelManager/setPanelPath';
import addItem, {
    AddItem
} from '~/frontend/src/actions/data/addItem';
import deleteItem, {
    DeleteItem
} from '~/frontend/src/actions/data/deleteItem';
import addReference, {
    AddReference
} from '~/frontend/src/actions/data/addReference';
import deleteReference, {
    DeleteReference
} from '~/frontend/src/actions/data/deleteReference';
import clearPanelProps from '~/frontend/src/actions/panelManager/clearPanelProps';
import cancelRequests, {
    CancelRequests
} from '~/frontend/src/actions/ajax/cancelRequests';


type Variables = {
    [ variable : string ] : any;
};


type SetParam = <K extends keyof ( ComponentProps & {
    rootItemId? : number;
    rootItemKey? : string;
    rootItemType? : string;
} )>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<( Omit<ComponentProps, 'rootItemId' | 'rootItemKey' | 'rootItemType'> & {
    rootItemId? : number;
    rootItemKey? : string;
    rootItemType? : string;
} )> ) => SetParamsAction;


type DefaultProps = BaseRequestParameters & {
    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode? : boolean;

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames? : boolean;

    saveConfig? : RequestConfig;

    contentPanelOnly? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...defaultBaseRequestParameters,

    rawMode               : false,
    showAdditionalRawNames: false,
    saveConfig            : {
        requestCommand: 'save' as string,
        requestModule : 'data' as string
    },
    contentPanelOnly      : false
};

type Props = {
    uid : string;
    dataContext : string;

    /**
     * The name of the view
     */
    viewName? : string;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    title? : string;

    /**
     * The title for the tab of the panel. If not set the title will be computed.
     */
    tabTitle? : string;

    /**
     * The title for the tab tooltip of the panel. If not set the title will be computed.
     */
    tooltipTitle? : string;

    /**
     * The icon to display the panel with. If not set the icon will be computed.
     */
    iconCls? : string;

    /**
     * The ItemId to filter data and metadata for.
     */
    menuWidth : number; //.isRequired,

    // the ciId of the root item
    rootItemId : number
    rootItemKey : string;
    rootItemType : string;

    viewKey : string;
    labelKey : string;

    requestParams? : {
        [ key : string ] : string;
    };

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    subPanelErrors? : { [ subPanelKey : string ] : ErrorData } | undefined;

    dirtyNodeIds? : string[];

    nodeIdToPanelKeyMap? : { [ nodeId : string ] : string };

    resumedNodes? : { [ nodeId : string ] : boolean };

    filter? : boolean,

    filterValue? : string,

    /**
     * The transformer for the module (component) state.
     * NOTE: Have to be set (in opposition to be a default) to be included in the state anyway!
     */
    moduleTransformer? : string;

    pathToSelect? : string[];

    additionalApiParams? : APIParams;

    referenceToCreateForNew? : ReferenceAttributesForCreation;

    onSave? : ( rootItemId : number, dataContext : string ) => void;
};

export type ComponentProps = DefaultProps & Props;

type StateProps = {

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    treeVariables? : Variables;

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveDataState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    setParam : SetParam;
    setParams : SetParams;
    setPanelPath : SetPanelPath;
    setElusiveData : SetElusiveData;
    clearElusiveData : ClearElusiveData;
    clearElusiveDataForSubpanels : ClearElusiveDataForSubpanels;
    loadData : LoadData;
    saveData : SaveData;
    setMessage : SetMessage;
    unsetMessage : UnsetMessage;
    setPanelDirty : SetPanelDirty;
    closePanel : ClosePanel;
    openPanel : OpenPanel;
    addItem : AddItem;
    deleteItem : DeleteItem;
    addReference : AddReference;
    deleteReference : DeleteReference;
    cancelRequests : CancelRequests;
};
type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
};

const Actions = { setParam, setParams, setPanelPath, setElusiveData, clearElusiveData, clearElusiveDataForSubpanels, loadData, saveData, setMessage, unsetMessage, setPanelDirty, closePanel, openPanel, addItem, deleteItem, addReference, deleteReference, cancelRequests } as any;

export type AllProps = Required<DefaultProps> & Props & StateProps & ActionProps;


type State = {
    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | JSX.Element | ( string | JSX.Element )[];
    errorMessage? : string | JSX.Element | ( string | JSX.Element )[];
    showDataNoLongerValidDialog : boolean;
    treeData? : TreeNodeData[];
    selectedKey? : string;
    selectedPayloadId? : number;
    selectedPayloadReferenceId? : number;
    expandedKeys? : TreeExpandedKeysType;
    rerenderToggle : number;
};


/**
 * Playground to test Tree implementation
 */
class TreeView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'TreeView';
    static panelTabTitle = 'YN.base.frontend.treeView.tabTitle';
    static panelTabIcon = 'itemTypeIcon:CFG:Tree';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.frontend.treeView';
    static panelPropsSchema = Joi.object()
        // panelSchema defaults
        .concat( requestBaseParametersSchema )
        .concat( Joi.object( {
            panelType              : Joi.string().valid( TreeView.panelType ),
            panelTabTitle          : Joi.string().default( TreeView.panelTabTitle ),
            panelTabIcon           : Joi.string().default( TreeView.panelTabIcon ),
            schema                 : Joi.string().ynSchema().description( 'Schema of the payload root item.' ),
            rootItemId             : Joi.number().description( 'Id of the payload root item.' ),
            rootItemType           : Joi.string().description( 'Item Type of the payload root item.' ),
            rootItemKey            : Joi.string().description( 'Key of the payload root item.' ),
            viewKey                : Joi.string().description( 'The key of the TreeviewItem.' ),
            labelKey               : Joi.string().description( 'The key of the Message used as label.' ),
            rawMode                : rawModeSchema,
            showAdditionalRawNames : showAdditionalRawNamesSchema,
            pathToSelect           : Joi.array().items(
                Joi.string()
            ),
            referenceToCreateForNew: Joi.object( {
                srcItemId     : Joi.number()
                    .required(),
                destItemId    : Joi.number()
                    .required(),
                referenceSetId: Joi.number()
                    .required(),
                statusId      : Joi.number()
                    .required()
            } ),
            onSave                 : Joi.function()
        } ) );

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await TreeView.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await TreeView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'dataContext' | 'viewKey' | 'viewName' | 'schema' | 'rootItemId'>, nodePath? : string[] ) : string | undefined {
        const item = global.data[ props.dataContext ]?.items?.[ props.rootItemId ?? 0 ];

        let retVal : string | undefined;

        if ( item && nodePath?.length ) {
            retVal = `#${TreeView.panelType}/${props.viewName ?? props.viewKey}/${props.schema}/${encodeURIComponent( item.displayAttributeValue )}`;

            nodePath.shift();
            if ( nodePath.length ) {
                retVal += `/${nodePath.map( nP => nP.replaceAll( '/', '%2F' ) ).join( '/' )}`;
            }
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {
                  pathToSelect: []
              },
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        let viewName : string | undefined;

        // params[ 0 ] = panelType

        // params[ 1 ] = viewKey
        if ( params?.[ 1 ] ) {
            viewName = params[ 1 ];
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.treeView.resolvePanelPropsFromURL.error.couldNotResolveViewName' ) );
        }

        // params[ 2 ] = schema
        if ( params?.[ 2 ] ) {
            retVal.schema = params[ 2 ] as Schema;
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.treeView.resolvePanelPropsFromURL.error.couldNotResolveSchema' ) );
        }

        // params[ 3 ] = rootItem.displayAttributeValue
        if ( params?.[ 3 ] ) {
            if ( viewName && retVal.schema ) {
                const displayAttributeValue = decodeURIComponent( params[ 3 ] );

                // resolve viewKey and rootItemId
                const res    = await xhr.post(
                          `${BASE_URL}/api/tree/getViewKeyAndRootItemIdByNameAndDisplayAttribute`,
                          {
                              schema: retVal.schema,
                              viewName,
                              displayAttributeValue
                          }
                      ),
                      result = res?.data as GetViewKeyAndRootItemIdByNameAndDisplayAttributeResult;

                if ( result ) {
                    if ( result.data?.viewKey ) {
                        retVal.viewKey = result.data.viewKey;
                    }
                    else {
                        stackError.add( new LocalizableError( 'YN.base.treeView.resolvePanelPropsFromURL.error.couldNotResolveViewKey', { name: viewName } ) );
                    }

                    if ( result.data?.rootItemId ) {
                        retVal.rootItemId = result.data.rootItemId;
                    }
                    else {
                        stackError.add( new LocalizableError( 'YN.base.treeView.resolvePanelPropsFromURL.error.couldNotResolveRootItemId', { attribute: displayAttributeValue } ) );
                    }

                    if ( result.data?.status ) {
                        retVal.status = result.data.status as Status;
                    }
                }
            }
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.treeView.resolvePanelPropsFromURL.error.couldNotResolveRootItem' ) );
        }

        // node path to open
        if ( params.length > 3 ) {
            for ( let i = 3; i < params.length; i++ ) {
                if ( params[ i ] ) {
                    retVal.pathToSelect!.push( params[ i ].replaceAll( '%2F', '/' ) );
                }
            }
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid,
                  subPanelProps,
                  subPanelErrors
              }                                        = propsWithDefaults,
              elusiveData                              = getState()?.elusiveData?.[ uid ],
              errorData                                = elusiveData?.errorData as PanelErrorData[ 'errorData' ];

        let errorCount = errorData?.count ?? 0;
        for ( const subPanelKey in subPanelErrors ) {
            const subPanelErrorData = subPanelErrors[ subPanelKey ];
            const shortSubPanelKey = subPanelKey.replace( uid + '_', '' );
            // check for form root as deleted forms has no formRoot but remaining errors under some circumstances
            // TODO: check why and fix
            const formRootItemId = subPanelProps[ shortSubPanelKey ]?.rootItemId;
            if ( formRootItemId ) {
                errorCount += ( subPanelErrorData?.count ?? 0 );
            }
        }

        // panel has errors -> show popup
        if ( errorCount ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );

            return Promise.resolve( false );
        }

        // panel has no errors -> save
        else {
            const saveDataHandler = async ( ...args : Parameters<SaveData> ) => {
                return dispatch( saveData( ...args ) );
            };

            return TreeView.doSaveData( uid, saveDataHandler, successMsgParams );
        }
    }

    static async doSaveData( uid : string, saveDataHandler : ActionProps[ 'saveData' ], successMsgParams : MsgParams | undefined = undefined ) : ReturnType<ActionProps[ 'saveData' ]> {
        const props                                              = getPanelProps( uid )?.panelProps as ComponentProps,
              treeUid                                            = `${uid}_tree`,
              elusiveData                                        = getPanelElusiveData( uid ),
              treeElusiveData                                    = getPanelElusiveData( treeUid ),
              propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  onSave,
                  rootItemId,
                  saveConfig,
                  schema,
                  subPanelProps
              }                                                  = propsWithDefaults,
              rootItemIdForSave                                  = rootItemId ?? elusiveData?.data?.rootItemId,
              treeProps                                          = subPanelProps?.[ 'tree' ] ?? {},
              {
                  selectedPayloadId,
                  selectedPayloadReferenceId
              }                                                  = treeProps,
              processedRequestParams : {
                  [ key : string ] : string
              }                                                  = TreeView.resolveRequestParameters( saveConfig.requestParams, schema, uid, rootItemId!, treeElusiveData?.variables );

        for ( const subPanelKey in subPanelProps ) {
            if ( subPanelKey !== 'tree' && subPanelKey.match( /--\d*$/ ) ) {
                ( store.dispatch as AppDispatch )( clearPanelProps( `${uid}_${subPanelKey}` ) );
                ( store.dispatch as AppDispatch )( clearElusiveData( `${uid}_${subPanelKey}` ) );
            }
        }

        const result = await ( store.dispatch as AppDispatch )(
            saveData(
                uid,
                schema,
                rootItemIdForSave,
                saveConfig.requestModule ?? defaultProps.saveConfig.requestModule,
                saveConfig.requestCommand ?? defaultProps.saveConfig.requestCommand,
                processedRequestParams,
                successMsgParams
            )
        );

        if ( result && result.success ) {
            // remap rootItemId id
            if ( rootItemIdForSave && result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ] ) {
                ( store.dispatch as AppDispatch )( setParam( uid, 'rootItemId', result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ].id ) );
            }
            // remap selectedPayloadId id
            if ( selectedPayloadId && result.data.itemMetaDataMapping[ String( selectedPayloadId ) as unknown as number ] ) {
                ( store.dispatch as AppDispatch )( setParam( treeUid, 'selectedPayloadId', result.data.itemMetaDataMapping[ String( selectedPayloadId ) as unknown as number ].id ) );
            }
            // remap selectedPayloadId id
            if ( selectedPayloadReferenceId && result.data.referenceMetaDataMapping[ String( selectedPayloadReferenceId ) as unknown as number ] ) {
                ( store.dispatch as AppDispatch )( setParam( treeUid, 'selectedPayloadReferenceId', result.data.referenceMetaDataMapping[ String( selectedPayloadReferenceId ) as unknown as number ].id ) );
            }
        }

        if ( onSave ) {
            // remap rootItem id
            if ( result && result.data?.itemMetaDataMapping?.[ String( rootItemIdForSave ) as unknown as number ] ) {
                onSave( result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ].id, uid );
            }
            else {
                onSave( rootItemIdForSave, uid );
            }
        }

        return result;
    }

    static resolveRequestParameters( requestParameters : RequestConfig[ 'requestParams' ] | undefined, schema : Schema, dataContext : string, rootItemId : number, variables? : Variables ) : {
        [ key : string ] : string
    } {
        const retVal : {
            [ key : string ] : string
        } = {};

        if ( requestParameters ) {
            const requestParamsKeys             = Object.keys( requestParameters ?? {} ),
                  messageTemplateStringResolver = TreeView.getMessageTemplateStringResolver(
                      dataContext,
                      rootItemId,
                      variables ?? {},
                      {
                          isDataModified: isDataModifiedRecursive( dataContext, schema ?? defaultBaseRequestParameters.schema, rootItemId )
                      }
                  );

            for ( const key of requestParamsKeys ) {
                const processedRequestParam = messageTemplateStringResolver( requestParameters[ key ] );

                if ( processedRequestParam !== 'undefined' ) {
                    retVal[ key ] = processedRequestParam;
                }
            }
        }

        return retVal;
    }

    static getMessageTemplateStringResolver( dataContext : string, rootItemId : number, variables : Variables, additionalResolverParameters? : {
        [ name : string ] : any
    } ) : ( templateString : string, rawMode? : boolean ) => string {
        const item = global.data[ dataContext ]?.items[ rootItemId ];

        let retVal : ( templateString : string, rawMode? : boolean ) => string = ( templateString : string, rawMode : boolean = false ) => templateString;

        if ( item ) {
            retVal = messageUtils.getMessageTemplateStringResolverForResolvedDataObjects(
                item,
                undefined,
                {
                    rootObject: {
                        item
                    }
                },
                {
                    variables: {
                        ...variables,
                        [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                            if ( hint === 'string' ) {
                                return nl2br( JSON.stringify( variables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                            }
                            else {
                                return null;
                            }
                        }
                    },
                    ...additionalResolverParameters
                }
            );
        }

        return retVal;
    }

    toast? : Toast | null;
    form? : FormClass | null;
    tree? : ItemTreeClass | null;

    treeUtils? : TreeUtils;

    panelConfigBelongsToNodeCirsIds : number[];
    panelConfigIsUsedForNewDynamicNodeCirsIds : number[];

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            isDirty                    : false,
            errorCount                 : 0,
            showDataNoLongerValidDialog: false,
            rerenderToggle             : 0
        };

        this.panelConfigBelongsToNodeCirsIds = metaModel.CFG
            .getReferenceSetsForItemTypesAndReferenceType( 'PanelConfig', 'TreeNode', 'belongsTo', 'src' )
            .concat( metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'PanelConfig', 'DynamicNode', 'belongsTo', 'src' ) )
            .map( rset => rset.id );
        this.panelConfigIsUsedForNewDynamicNodeCirsIds = metaModel.CFG
            .getReferenceSetsForItemTypesAndReferenceType( 'PanelConfig', 'DynamicNode', 'isUsedForNew', 'src' )
            .map( rset => rset.id );

        this.buildPanelPathFromProps = memoize( this.buildPanelPathFromProps );
        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.getPanelConfigForNode = memoize(
            this.getPanelConfigForNode,
            ( newArgs, lastArgs ) =>
                newArgs[ 0 ] === lastArgs[ 0 ]              // selectedNodeId
                && newArgs[ 1 ] === lastArgs[ 1 ]           // payloadItem (Item reference)
                && newArgs[ 3 ] === lastArgs[ 3 ]           // payloadReference (Reference reference)
                // replacements is a fresh object literal on every render -> compare by value
                && _.isEqual( newArgs[ 2 ], lastArgs[ 2 ] )
        );
        this.getStringifiedRequestParameters = memoize( this.getStringifiedRequestParameters );
        this.setPanelInfo = memoize( this.setPanelInfo );

        this.onCopyXmlButtonClick = this.onCopyXmlButtonClick.bind( this );
        this.onDownloadXmlButtonClick = this.onDownloadXmlButtonClick.bind( this );
        this.onFormDirtyChanged = this.onFormDirtyChanged.bind( this );
        this.onFormErrorDataChanged = this.onFormErrorDataChanged.bind( this );
        this.onGetTreeUtils = this.onGetTreeUtils.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
        this.onSaveTreeData = this.onSaveTreeData.bind( this );
        this.onSetTreeVariables = this.onSetTreeVariables.bind( this );
        this.onTreeDataChange = this.onTreeDataChange.bind( this );
        this.onTreeDataLoad = this.onTreeDataLoad.bind( this );
        this.onTreeSelectionChange = this.onTreeSelectionChange.bind( this );
        this.onTreeToggleNode = this.onTreeToggleNode.bind( this );
    }

    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  dataContext,
                  schema,
                  uid,
                  rootItemId,
                  subPanelProps,
                  subPanelErrors
              }                       = props,
              treeProps               = subPanelProps?.[ 'tree' ] ?? {},
              {
                  selectedPayloadId,
                  selectedPayloadReferenceId
              }                       = treeProps,
              retVal : Partial<State> = {
                  errorCount: 0,
                  isDirty   : rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId, false ) : isDataModified( dataContext, schema! ),
                  selectedPayloadId,
                  selectedPayloadReferenceId
              };

        if ( subPanelErrors ) {
            let errorTooltipParts : ( string | JSX.Element | JSX.Element[] )[] = [],
                errorMessageParts : ( string | JSX.Element | JSX.Element[] )[] = [];

            for ( const subPanelKey of Object.keys( subPanelErrors ) ) {
                // TODO: change error handling to prevent duplicate errors ( _attributesForm )
                if ( ( subPanelKey as string ).match( /\d+$/ ) ) {
                    const shortSubPanelKey = ( subPanelKey as string ).replace( uid + '_', '' );
                    const formRootItemId = subPanelProps[ shortSubPanelKey ]?.rootItemId;

                    if ( formRootItemId && !global.data[ dataContext ]?.items[ formRootItemId ]?.isDeleted() ) {
                        retVal.errorCount! += subPanelErrors[ subPanelKey ].count ?? 0;
                        if ( subPanelErrors[ subPanelKey ].tooltip ) {
                            errorTooltipParts = errorTooltipParts.concat( subPanelErrors[ subPanelKey ].message! );
                        }
                        if ( subPanelErrors[ subPanelKey ].message ) {
                            errorMessageParts = errorMessageParts.concat( subPanelErrors[ subPanelKey ].message! );
                        }
                    }
                }
            }

            retVal.errorTooltip = <div key="error" className="yn-tooltip-error">
                <div className="yn-tooltip-header">
                    {__( 'YN.base.common.tooltip.error.title' )}
                </div>
                {errorTooltipParts}
            </div>;

            retVal.errorMessage = <>
                <div className="yn-error-message">
                    {__( 'YN.base.tree.couldNotSaveWhileErrors.text' )}
                </div>
                <hr className="yn-error-ruler"/>
                <div className="yn-error-suberror">
                    {errorMessageParts}
                </div>
            </>;
        }

        return retVal;
    }

    componentDidMount() {
        const {
                  rootItemId,
                  referenceToCreateForNew,
                  schema,
                  uid
              } = this.props;

        if ( rootItemId < 0 && referenceToCreateForNew ) {
            if ( referenceToCreateForNew.srcItemId === 0 ) {
                referenceToCreateForNew.srcItemId = rootItemId;
            }
            else {
                referenceToCreateForNew.destItemId = rootItemId;
            }

            this.props.addReference( uid, referenceToCreateForNew, schema );

            this.props.setParam( uid, 'referenceToCreateForNew', undefined );
        }

        // initialize state with tree data
        if ( this.tree && this.state.selectedKey !== this.tree.props.selectedKey ) {
            this.setState( {
                treeData                  : this.tree.props.treeData,
                selectedKey               : this.tree.props.selectedKey,
                selectedPayloadId         : this.tree.props.selectedPayloadId,
                selectedPayloadReferenceId: this.tree.props.selectedPayloadReferenceId,
                expandedKeys              : this.tree.props.expandedKeys
            } );
        }
        else {
            this.afterRender();
        }
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( lastProps : AllProps, lastState : State ) {
        // update state with tree data (should never have executed since there are listeners for changes)
        if (
            this.tree
            && (
                this.state.selectedKey !== this.tree.props.selectedKey
                || this.state.treeData !== this.tree.props.treeData
                || this.state.selectedPayloadId !== this.tree.props.selectedPayloadId
                || this.state.selectedPayloadReferenceId !== this.tree.props.selectedPayloadReferenceId
                || this.state.expandedKeys !== this.tree.props.expandedKeys
            )
        ) {
            this.setState( {
                treeData                  : this.tree.props.treeData,
                selectedKey               : this.tree.props.selectedKey,
                selectedPayloadId         : this.tree.props.selectedPayloadId,
                selectedPayloadReferenceId: this.tree.props.selectedPayloadReferenceId,
                expandedKeys              : this.tree.props.expandedKeys
            } );
        }

        if ( lastProps.rootItemId && lastProps.rootItemId !== this.props.rootItemId ) {
            this.onReloadButtonClick();
        }

        this.afterRender();
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        const changedProps = objectUtils.getDiffKeys(
                  this.props,
                  nextProps,
                  [ 'subPanelProps', 'elusiveData', 'treeVariables' ]
              ),
              changedState = objectUtils.getDiffKeys(
                  this.state,
                  nextState,
                  [ 'treeData' ]
              ),
              {
                  pathToSelect
              }            = nextProps,
              {
                  treeData,
                  expandedKeys: expandedKeysOrig
              }            = nextState;

        if ( !changedProps.length && !changedState.length ) {
            return false;
        }

        let retVal = true,
            {
                isDirty
            }      = nextState;

        if (
            !isDirty
            && nextProps.rootItemId
            && nextProps.saveConfig.requestParams
        ) {
            const rootItemDataVersion          = nextProps.elusiveData?.data?.items?.[ nextProps.rootItemId ] ?? 0,
                  stringifiedSaveRequestParams = this.getStringifiedRequestParameters( nextProps.saveConfig.requestParams, nextProps.schema, nextProps.uid, nextProps.rootItemId, nextProps.treeVariables, rootItemDataVersion );

            if ( stringifiedSaveRequestParams !== '{}' ) {
                isDirty = true;
            }
        }

        this.props.setPanelDirty( this.props.uid, isDirty );

        // resolve selectedKey from pathToSelect, add all path nodes to expandedKeys and set both
        if ( this.tree && treeData?.length && pathToSelect ) {
            let selectedKeyToSelect : string | undefined,
                expandedKeysToSelect : TreeExpandedKeysType | undefined;

            // root node if no node path given
            if ( !pathToSelect.length || pathToSelect[ 0 ] === 'root' ) {
                selectedKeyToSelect = treeData[ 0 ].key;
            }
            else {
                const recursiveResolveKey = ( nodeTreeData : TreeNodeData[], path : string[] ) : {
                    selectedKey : string | undefined;
                    expandedKeys : TreeExpandedKeysType | undefined;
                } => {
                    const innerRetVal : {
                              selectedKey : string | undefined;
                              expandedKeys : TreeExpandedKeysType;
                          }     = {
                              selectedKey : undefined,
                              expandedKeys: expandedKeysOrig ?? {}
                          },
                          label = path.shift();

                    for ( const data of nodeTreeData ) {
                        if ( data.label === label ) {
                            if ( path.length && data.children?.length ) {
                                const {
                                          selectedKey,
                                          expandedKeys
                                      } = recursiveResolveKey( data.children, path );

                                innerRetVal.selectedKey = selectedKey;
                                innerRetVal.expandedKeys = {
                                    ...innerRetVal.expandedKeys,
                                    ...expandedKeys,
                                    [ data.id ]: true
                                };
                            }
                            else {
                                innerRetVal.selectedKey = data.key;
                            }

                            break;
                        }
                    }

                    return innerRetVal;
                };

                ( { selectedKey: selectedKeyToSelect, expandedKeys: expandedKeysToSelect } = recursiveResolveKey( treeData, ( [] as string[] ).concat( pathToSelect ) ) );
            }

            if ( selectedKeyToSelect ) {
                this.tree.onSelectTreeNode( { originalEvent: null, value: selectedKeyToSelect } as unknown as TreeSelectionEvent );
                this.tree.onToggleTreeNode( { originalEvent: null, value: expandedKeysToSelect ?? { [ treeData[ 0 ].id ]: true } } as unknown as TreeExpandedEvent );
            }

            this.props.setParam( this.props.uid, 'pathToSelect', undefined );

            retVal = false;
        }

        return retVal;
    }

    buildPanelPathFromProps( dataContext : AllProps['dataContext'], schema : AllProps['schema'], viewKey : AllProps['viewKey'], viewName : AllProps['viewName'], rootItemId : AllProps['rootItemId'], state : State = {} as State ) {
        const {
                  treeData,
                  selectedPayloadId,
                  selectedPayloadReferenceId
              }           = state,
              selectedKey = ( state.selectedKey ? state.selectedKey : undefined )
                  ?? this.treeUtils?.getDynamicNodeIdByPayloadReferenceId( selectedPayloadReferenceId ?? 0 )?.[ 0 ]
                  ?? this.treeUtils?.getDynamicNodeIdByPayloadId( selectedPayloadId ?? 0 )?.[ 0 ]
                  ?? treeData?.[ 0 ]?.key;

        let nodePath : string[] = [];

        // build path to selected node
        if ( treeData?.length && selectedKey ) {
            const recursiveResolvePath = ( nodeTreeData : TreeNodeData[] ) : string[] => {
                let path : string[] = [];

                for ( const data of nodeTreeData ) {
                    if ( data.key === selectedKey ) {
                        path.push( data.label! );
                        break;
                    }
                    else if ( data.children?.length ) {
                        path = recursiveResolvePath( data.children );

                        if ( path.length ) {
                            path.unshift( data.label! );
                            break;
                        }
                    }
                }

                return path;
            };

            if ( treeData ) {
                nodePath = recursiveResolvePath( treeData );
            }
        }

        return TreeView.buildPanelPathFromProps( { dataContext, schema, viewKey, viewName, rootItemId }, nodePath )
    }

    getStringifiedRequestParameters( requestParameters : RequestConfig[ 'requestParams' ] | undefined, schema : Schema, dataContext : string, rootItemId : number, variables : Variables | undefined = undefined, rootItemDataVersion : number ) {
        return JSON.stringify( TreeView.resolveRequestParameters( requestParameters, schema, dataContext, rootItemId, variables ) );
    }

    afterRender( props : AllProps = this.props as AllProps ) {
        const {
                  uid,
                  dataContext,
                  schema,
                  viewKey,
                  viewName,
                  rootItemId: rootItemIdOrig,
                  rootItemType,
                  iconCls,
                  tabTitle    : tabTitleOrig,
                  tooltipTitle: tooltipTitleOrig,
                  elusiveData,
                  intercomMessages
              }                                                   = props,
              {
                  isDirty,
                  errorCount
              }                                                   = this.state,
              rootItemId                                          = rootItemIdOrig ?? elusiveData?.data?.rootItemId,
              itemTypeIconConfig : ItemTypeIconConfig | undefined = rootItemId
                  ? {
                      type: 'byItemId',
                      dataContext,
                      id  : rootItemId
                  }
                  : rootItemType
                      ? {
                          type    : 'byItemType',
                          schema,
                          itemType: rootItemType
                      }
                      : undefined,
              icon                                                = this.getPanelIcon( TreeView.panelTabIcon, iconCls, itemTypeIconConfig ),
              itemTitleConfig : ItemTitleConfig | undefined       = rootItemId
                  ? {
                      dataContext,
                      id: rootItemId
                  }
                  : undefined,
              tabTitle                                            = this.resolveTitle( tabTitleOrig ),
              title                                               = this.getPanelTitle( TreeView.panelTabTitle, tabTitle, itemTitleConfig ),
              tooltipTitle                                        = this.resolveTitle( tooltipTitleOrig ),
              path                                                = this.buildPanelPathFromProps( dataContext, schema, viewKey, viewName, rootItemId, this.state );

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, tooltipTitle, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] && !_.isString( intercomMessages.toast[ key ] ) ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage?.( this.props.uid, 'toast', key as string );
            }
        }
    }

    resolveTitle( title? : string ) {
        const {
                  dataContext,
                  schema,
                  viewKey,
                  viewName,
                  rootItemId: rootItemIdOrig,
                  elusiveData
              }          = this.props,
              rootItemId = rootItemIdOrig ?? elusiveData?.data?.rootItemId;

        let retVal = title;

        if ( retVal ) {
            const rootItem = global.data[ dataContext ]?.items?.[ rootItemId ];

            if ( rootItem ) {
                const labelTemplateResolver = messageUtils.getMessageTemplateStringResolver( rootItem ),
                      itemStatusStyle       = getStatusColorStyleString( rootItem.statusId, schema );

                retVal = labelTemplateResolver( __( retVal, { viewKey, viewName: viewName ?? '', itemStatusStyle } ) );
            }
        }

        return retVal;
    }

    getPanelConfigForNode( selectedNodeId : number, payloadItem? : Item, replacements? : {
        [ placeholder : string ] : string | number | undefined
    }, payloadReference? : Reference ) : PanelProps | undefined {
        const metaContext         = this.props.dataContext + '-meta',
              orderPropertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0,
              dynamicNodeId       = this.treeUtils?.getDynamicNodeIdByPayloadReferenceId( payloadReference?.id ?? 0 )[ 0 ]
                  ?? this.treeUtils?.getDynamicNodeIdByPayloadId( payloadItem?.id ?? 0 )[ 0 ],
              dynamicNode         = dynamicNodeId ? this.treeUtils?.getNodeById( dynamicNodeId ) : undefined;

        let retVal : PanelProps | undefined,
            panelConfigItems : Item[] | undefined;

        // check for explicit configured panelConfig
        // get panelConfigItems for new dynamicNode if item is new
        if ( payloadItem?.isNew() ) {
            panelConfigItems = dataUtils.findReferences( metaContext, this.panelConfigIsUsedForNewDynamicNodeCirsIds, undefined, selectedNodeId )
                // reduce to panelConfigItems the current user has permission for
                ?.reduce(
                    ( innerRetVal, ref ) => {
                        if ( ref.srcItem && user?.hasPermissionOnPanelConfig( ref.srcItemId ) ) {
                            if ( !innerRetVal ) {
                                innerRetVal = [];
                            }

                            innerRetVal.push( ref.srcItem );
                        }

                        return innerRetVal;
                    },
                    undefined as Item[] | undefined
                )
                // sort panelConfigItems by property Order
                ?.sort(
                    ( item1, item2 ) : -1 | 0 | 1 => {
                        const item1Order = Number( item1.properties.get( orderPropertyTypeId )?.value ?? Number.POSITIVE_INFINITY ),
                              item2Order = Number( item2.properties.get( orderPropertyTypeId )?.value ?? Number.POSITIVE_INFINITY );

                        if ( item1Order > item2Order ) {
                            return 1;
                        }
                        else if ( item1Order < item2Order ) {
                            return -1;
                        }
                        else {
                            return 0;
                        }
                    }
                );
        }

        // get panelConfigItems for dynamicNode if no one
        if ( !panelConfigItems?.length ) {
            panelConfigItems = dataUtils.findReferences( metaContext, this.panelConfigBelongsToNodeCirsIds, undefined, selectedNodeId )
                // reduce to panelConfigItems the current user has permission for
                ?.reduce(
                    ( innerRetVal, ref ) => {
                        if ( ref.srcItem && user?.hasPermissionOnPanelConfig( ref.srcItemId ) ) {
                            if ( !innerRetVal ) {
                                innerRetVal = [];
                            }

                            innerRetVal.push( ref.srcItem );
                        }

                        return innerRetVal;
                    },
                    undefined as Item[] | undefined
                )
                // sort panelConfigItems by property Order
                ?.sort(
                    ( item1, item2 ) : -1 | 0 | 1 => {
                        const item1Order = Number( item1.properties.get( orderPropertyTypeId )?.value ?? Number.POSITIVE_INFINITY ),
                              item2Order = Number( item2.properties.get( orderPropertyTypeId )?.value ?? Number.POSITIVE_INFINITY );

                        if ( item1Order > item2Order ) {
                            return 1;
                        }
                        else if ( item1Order < item2Order ) {
                            return -1;
                        }
                        else {
                            return 0;
                        }
                    }
                );
        }

        if ( panelConfigItems?.length ) {
            retVal = this.props.user?.getPanelConfig( panelConfigItems[ 0 ].id )
        }

        // if no explicit config is set, get default for payload itemType (if applicable)
        if ( !retVal ) {
            retVal = payloadItem
                ? this.props.user?.getPanelPropsForItemType( this.props.schema, payloadItem.itemTypeId, [ 'ChartDesignerView', 'FormDesignerView', 'OverviewDesignerView', 'TreeView', 'ViewDesigner' ] )[ 0 ]
                : undefined;
        }

        if ( retVal && replacements ) {
            let stringified = JSON.stringify( retVal );
            for ( const placeholder of Object.keys( replacements ) ) {
                if ( replacements[ placeholder ] !== undefined ) {
                    stringified = stringified.replaceAll( '${' + placeholder + '}', replacements[ placeholder ] as string );
                }
            }
            try {
                retVal = JSON.parse( stringified );
            }
            catch ( e ) {
                // silent
            }
        }


        // dynamicNode settings for panelConfig
        if ( retVal && dynamicNode?.data.ciItem ) {
            if ( dynamicNode.data.ciItem.readOnly === true ) {
                retVal.readOnly = dynamicNode.data.ciItem.readOnly;
            }
        }

        // add referenceToCreateForNew and onSave handler for Designer- and TreeView panelConfig
        if (
            (
                retVal?.panelType.match( /DesignerView$/ )
                || retVal?.panelType.match( /TreeView$/ )
            )
            && payloadItem?.isNew()
            && payloadReference?.isNew()
        ) {
            const referenceAttributesForCreation = {
                srcItemId     : payloadReference.srcItemId === payloadItem.id ? 0 : payloadReference.srcItemId,
                destItemId    : payloadReference.destItemId === payloadItem.id ? 0 : payloadReference.destItemId,
                referenceSetId: payloadReference.referenceSetId,
                statusId      : payloadReference.statusId
            };

            retVal.referenceToCreateForNew = referenceAttributesForCreation;
            retVal.onSave = this.onExternalViewSave.bind( this, referenceAttributesForCreation );
        }

        return retVal;
    }

    async onCopyXmlButtonClick( e : MouseEvent<HTMLButtonElement> ) {
        const dataContext = this.props.dataContext + ( keyboardUtils.cmdKey( e ) ? '-meta' : '' );
        const treePayloadIds = this.treeUtils?.getPayloadIdsOfNodes();
        if ( this.props.rootItemType === 'YnItemType' ) {
            await navigator.clipboard.writeText(
                getMetaModelXmlForContext(
                    dataContext,
                    undefined,
                    undefined,
                    true,
                    this.props.rootItemId
                )
            );
        }
        else {
            await navigator.clipboard.writeText(
                getXmlForContext(
                    dataContext,
                    treePayloadIds,
                    undefined,
                    true,
                    true
                )
            );
        }

        this.props.setMessage?.(
            this.props.uid,
            'toast',
            'xmlCopiedToClipboard',
            {
                severity: 'info',
                summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                detail  : __( 'YN.base.common.xml.dataCopiedToClipboard.text' )
            }
        );
    }

    onDownloadXmlButtonClick( e : MouseEvent<HTMLButtonElement> ) {
        const dataContext = this.props.dataContext + ( keyboardUtils.cmdKey( e ) ? '-meta' : '' ),
              rootItemId  = this.props.rootItemId ?? this.props.elusiveData?.data?.rootItemId,
              rootItem    = global.data[ dataContext ]?.items?.[ rootItemId ];
        const treePayloadIds = this.treeUtils?.getPayloadIdsOfNodes();
        let fileName = rootItem?.key ?? this.props.viewKey;

        if ( this.props.rootItemType === 'YnItemType' ) {
            fileDownload(
                getMetaModelXmlForContext(
                    dataContext,
                    undefined,
                    undefined,
                    true,
                    this.props.rootItemId
                ),
                fileName + '-metamodel',
                'xml'
            );
        }
        else {
            if ( keyboardUtils.cmdKey( e ) ) {
                fileName += '-meta';
            }
            fileDownload(
                getXmlForContext(
                    dataContext,
                    treePayloadIds,
                    undefined,
                    true
                ),
                fileName,
                'xml'
            );
        }
    }

    onReloadButtonClick() {
        const {
                  dataContext,
                  uid,
                  subPanelProps,
                  elusiveData
              }          = this.props as AllProps,
              rootItemId = this.props.rootItemId ?? elusiveData?.data?.rootItemId ?? 0;

        this.props.clearElusiveData?.( uid );
        this.props.clearElusiveDataForSubpanels?.( uid );
        this.props.cancelRequests?.( uid );

        // if tree for exiting item, delete complete data context
        if ( rootItemId > 0 ) {
            delete global.data[ dataContext ];
        }
        // if tree for new item, delete all but not the root item
        else {
            for ( const itemId in global.data[ dataContext ].items ) {
                const item = global.data[ dataContext ].items[ itemId ];
                if ( item.id < 0 && item.id !== rootItemId ) {
                    item.delete();
                }
            }
        }

        this.props.setParams( uid, {
            subPanelProps      : {
                tree: {
                    ...subPanelProps.tree,
                    resumedNodes              : {},
                    selectedNodeId            : subPanelProps.tree?.selectedPayloadId > 0 ? subPanelProps.tree?.selectedNodeId : 0,
                    selectedPayloadId         : subPanelProps.tree?.selectedPayloadId > 0 ? subPanelProps.tree?.selectedPayloadId : 0,
                    selectedPayloadReferenceId: subPanelProps.tree?.selectedPayloadId > 0 ? subPanelProps.tree?.selectedPayloadReferenceId : 0
                }
            },
            subPanelErrors     : {},
            dirtyNodeIds       : [],
            nodeIdToPanelKeyMap: {},
            resumedNodes       : {}
        } );

        this.props.setMessage?.( this.props.uid + '_tree', 'notification', 'reload', rootItemId );
    }

    onTreeDataLoad( responseData : any ) {
        const {
                  dataContext,
                  uid,
                  additionalApiParams
              } = this.props;

        this.props.setElusiveData( uid, { lastSaveTimestamp: 0 } );
        this.props.setParam( this.props.uid, 'dirtyNodeIds', undefined );

        if ( additionalApiParams?.data && typeof additionalApiParams?.data === 'object' && additionalApiParams.data?.dataContext && global.data[ additionalApiParams.data.dataContext ] ) {
            for ( const item of Object.values( global.data[ additionalApiParams.data.dataContext ].items ) ) {
                if ( !global.data[ dataContext + '-meta' ].items[ item.id ] ) {
                    item.clone( undefined, undefined, dataContext + '-meta' );
                }
            }
            for ( const reference of Object.values( global.data[ additionalApiParams.data.dataContext ].references ) ) {
                if ( !global.data[ dataContext + '-meta' ].references[ reference.id ] ) {
                    reference.clone( undefined, undefined, dataContext + '-meta' );
                }
            }
        }

        window.setTimeout(
            () => {
                const path = this.buildPanelPathFromProps( this.props.dataContext, this.props.schema, this.props.viewKey, responseData.data.viewName, this.props.rootItemId );

                if ( path ) {
                    this.props.setPanelPath( this.props.uid, path );
                }
            },
            15
        );
    }

    onTreeDataChange( treeData : TreeNodeData[] | undefined ) {
        this.setState( { treeData } );
    }

    onTreeSelectionChange( selectedKey : string ) {
        this.setState( { selectedKey } );
    }

    onTreeToggleNode( expandedKeys : TreeExpandedKeysType ) {
        this.setState( { expandedKeys } );
    }

    onFormErrorDataChanged( formUid : string ) {
        const
            subPanelErrors = getSubPanelErrors( this.props.uid ),
            treeProps      = this.props.subPanelProps?.[ 'tree' ],
            selectedKey    = treeProps?.selectedKey
                || this.treeUtils?.getDynamicNodeIdByPayloadId( treeProps?.selectedPayloadId ?? treeProps?.rootItemId )[ 0 ]
                || 'root';

        if ( selectedKey ) {
            const nodeIdToPanelKeyMap = { ...this.props.nodeIdToPanelKeyMap, [ selectedKey as string ]: formUid };

            this.props.setParams( this.props.uid, { subPanelErrors, nodeIdToPanelKeyMap } );
        }
        else {
            this.props.setParams( this.props.uid, { subPanelErrors } );
        }
    }

    onFormDirtyChanged( formUid : string, dirty : boolean ) {
        const treeProps   = this.props.subPanelProps?.[ 'tree' ],
              selectedKey = treeProps?.selectedKey
                  || this.treeUtils?.getDynamicNodeIdByPayloadId( treeProps?.selectedPayloadId ?? treeProps?.rootItemId )[ 0 ]
                  || 'root';

        if ( selectedKey ) {
            const dirtyNodeIds = ( [] as string[] ).concat( this.props.dirtyNodeIds ?? [] );

            let changed : boolean = false;

            if ( dirty && !dirtyNodeIds.includes( selectedKey ) ) {
                dirtyNodeIds.push( selectedKey );
                changed = true;
            }
            else if ( !dirty && dirtyNodeIds.includes( selectedKey ) ) {
                dirtyNodeIds.splice( dirtyNodeIds.findIndex( nodeId => nodeId === selectedKey ), 1 );
                changed = true;
            }

            if ( changed || this.props.nodeIdToPanelKeyMap?.[ selectedKey as string ] !== formUid ) {
                const nodeIdToPanelKeyMap = { ...this.props.nodeIdToPanelKeyMap, [ selectedKey as string ]: formUid };

                this.props.setParams( this.props.uid, { dirtyNodeIds, nodeIdToPanelKeyMap } );
            }
        }
    }

    /**
     * Handler for getting TreeUtils of ItemTree
     *
     * @param uid
     * @param treeUtils
     */
    onGetTreeUtils( uid : string, treeUtils : TreeUtils ) {
        this.treeUtils = treeUtils;
        // force rerender
        this.setState( { rerenderToggle: this.state.rerenderToggle + 1 } );
    }

    onSetTreeVariables( variables : Variables ) {
        const {
                  uid,
                  treeVariables
              }                = this.props,
              changedVariables = objectUtils.getDiffKeys(
                  treeVariables ?? {},
                  variables ?? {}
              );

        if (
            changedVariables.length
            && (
                changedVariables.length > 1
                || changedVariables[ 0 ] !== 'lastSaveTimestamp'
            )
            && JSON.stringify( treeVariables ?? {} ) !== JSON.stringify( variables ?? {} )
        ) {
            this.props.setElusiveData( `${uid}_tree`, { variables: variables } );
        }
    }

    async onSaveTreeData() : Promise<SaveDataResult | false> {
        const saveDataHandler = async ( ...args : Parameters<SaveData> ) => {
            return this.props.saveData( ...args );
        };

        return TreeView.doSaveData( this.props.uid, saveDataHandler );
    }

    onExternalViewSave( referenceAttributesForCreation : ReferenceAttributesForCreation, viewRootItemId : number, viewDataContext : string ) {
        const {
                  uid,
                  dataContext,
                  subPanelProps
              }            = this.props,
              viewRootItem = global.data[ viewDataContext ]?.items?.[ viewRootItemId ];

        let selectedReferenceId : number | undefined;

        if ( viewRootItem ) {
            this.props.addItem( dataContext, viewRootItem.toJSON( 'all' ) as ItemAttributesForCreation );

            for ( const referenceId of viewRootItem.references ) {
                const reference = global.data[ viewDataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.referenceSetId === referenceAttributesForCreation.referenceSetId
                    && (
                        reference.destItemId === referenceAttributesForCreation.destItemId
                        || reference.srcItemId === referenceAttributesForCreation.srcItemId
                    )
                ) {
                    selectedReferenceId = reference.id;
                    this.props.addReference( dataContext, reference.toJSON( 'all' ) as ReferenceAttributesForCreation );
                    break;
                }
            }

            // not visible
            this.props.setParams( uid, {
                subPanelProps: {
                    ...subPanelProps,
                    tree: {
                        ...subPanelProps?.tree,
                        treeData                  : undefined,
                        selectedPayloadId         : viewRootItem.id,
                        selectedPayloadReferenceId: selectedReferenceId ?? 0,
                        selectedKey               : '',
                        selectedNodeId            : 0
                    }
                }
            } );
        }
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  additionalApiParams,
                  dataContext,
                  uid,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  menuWidth,
                  subPanelProps,
                  viewKey,
                  title: titleOrig,
                  iconCls,
                  rootItemKey,
                  rootItemType,
                  nodeIdToPanelKeyMap,
                  subPanelErrors,
                  dirtyNodeIds,
                  elusiveData,
                  treeVariables,
                  saveConfig,
                  contentPanelOnly
              }                                                   = this.props as AllProps,
              {
                  errorCount,
                  errorTooltip,
                  errorMessage,
                  isDirty,
                  showDataNoLongerValidDialog
              }                                                   = this.state,
              treeProps                                           = subPanelProps?.[ 'tree' ] ?? {},
              {
                  selectedPayloadId,
                  selectedPayloadReferenceId
              }                                                   = treeProps,
              attributesPanelProps                                = subPanelProps?.[ 'attributes' ],
              rawMode                                             = false,
              showAdditionalRawNames                              = false,
              rootItemId                                          = this.props.rootItemId ?? elusiveData?.data?.rootItemId,
              rootItem                                            = global.data[ dataContext ]?.items?.[ rootItemId ],
              itemTypeIconConfig : ItemTypeIconConfig | undefined = rootItemId
                  ? {
                      type: 'byItemId',
                      dataContext,
                      id  : rootItemId
                  }
                  : rootItemType
                      ? {
                          type    : 'byItemType',
                          schema,
                          itemType: rootItemType
                      }
                      : undefined,
              icon                                                = this.getPanelIcon( TreeView.panelTabIcon, iconCls, itemTypeIconConfig ),
              itemTitleConfig : ItemTitleConfig | undefined       = rootItemId
                  ? {
                      dataContext,
                      id: rootItemId
                  }
                  : undefined,
              title                                               = this.getPanelTitle( elusiveData?.metaData?.treeData?.[ 0 ]?.data?.name ?? viewKey, this.resolveTitle( titleOrig ), itemTitleConfig );

        let selectedKey    = treeProps.selectedKey,
            selectedNodeId = treeProps.selectedNodeId,
            item : Item | undefined,
            reference : Reference | undefined,
            parentPayloadId : number | undefined,
            panelConfig : PanelProps | undefined,
            formUid : string | undefined,
            formProps : any | undefined;

        if ( this.treeUtils ) {
            // select root item if selectedPayloadId isn't defined
            if ( selectedPayloadId || ( !selectedNodeId && rootItemId ) ) {
                item = global.data[ dataContext ]?.items?.[ selectedPayloadId || rootItemId ];
            }

            if ( selectedPayloadReferenceId ) {
                reference = global.data[ dataContext ]?.references?.[ selectedPayloadReferenceId ];
            }

            if ( !selectedNodeId && ( selectedPayloadId || selectedPayloadReferenceId || rootItemId ) ) {
                const dynamicNodeId = this.treeUtils.getDynamicNodeIdByPayloadReferenceId( selectedPayloadReferenceId )[ 0 ]
                          ?? this.treeUtils.getDynamicNodeIdByPayloadId( selectedPayloadId )[ 0 ]
                          ?? this.treeUtils.getDynamicNodeIdByPayloadId( rootItemId )[ 0 ],
                      dynamicNode   = dynamicNodeId ? this.treeUtils.getNodeById( dynamicNodeId ) : undefined;

                selectedNodeId = dynamicNode?.data?.itemId;
                selectedKey = dynamicNodeId;
            }

            const parentNode = this.treeUtils?.getNodeById( this.treeUtils?.getNodeById( selectedKey || this.treeUtils?.getDynamicNodeIdByPayloadId( selectedPayloadId )?.[ 0 ] )?.data?.parentNodeId ?? '' );

            parentPayloadId = parentNode?.data.payloadId || parentNode?.data.delegatedPayloadId || rootItemId;

            const parentItem = global.data[ dataContext ]?.items?.[ parentPayloadId ?? 0 ];

            panelConfig = this.getPanelConfigForNode(
                selectedNodeId,
                item,
                {
                    nodeItemId    : item?.id,
                    nodeItemKey   : item?.key,
                    nodeItemType  : item?.itemType.name,
                    parentItemId  : parentPayloadId,
                    parentItemKey : parentItem?.key,
                    parentItemType: parentItem?.itemType.name,
                    rootItemId,
                    rootItemKey   : rootItem?.key,
                    rootItemType  : rootItem?.itemType.name
                },
                reference
            );

            // `-${item?.id ?? selectedNodeId}` this part is important!
            formUid = ( panelConfig?.viewKey?.replace( /[_:]/g, '-' ) ?? panelConfig?.panelType?.toLowerCase() ?? 'form' ) + `-${item?.id ?? selectedNodeId}`;
            formProps = subPanelProps?.[ formUid ];

            // open panelConfig as separate panel if
            if ( panelConfig?.panelManagerConfig?.openAsPanel ) {

                // delete new item and reference
                if ( reference?.isNew() ) {
                    this.props.deleteReference( dataContext, reference.id, true, undefined, false );
                }
                if ( item?.isNew() ) {
                    this.props.deleteItem( dataContext, item.id, true, undefined, false, false );
                }

                // clear selection
                this.props.setParams( uid, {
                    subPanelProps: {
                        ...subPanelProps,
                        tree: {
                            ...treeProps,
                            selectedKey               : subPanelProps?.tree?.lastSelectionParameters?.selectedKey ?? '',
                            selectedNodeId            : subPanelProps?.tree?.lastSelectionParameters?.selectedNodeId ?? 0,
                            selectedPayloadId         : subPanelProps?.tree?.lastSelectionParameters?.selectedPayloadId ?? 0,
                            selectedPayloadReferenceId: subPanelProps?.tree?.lastSelectionParameters?.selectedPayloadReferenceId ?? 0
                        }
                    }
                } );

                // open panel
                const openPanelConfig = { ...panelConfig } as PanelPropsByPanelType;
                setTimeout(
                    () => {
                        this.props.openPanel( openPanelConfig );
                    },
                    10
                );

                // clear item and panelConfig
                panelConfig = undefined;
                item = undefined;
            }
        }

        return <>
            {!contentPanelOnly &&
                <Dialog
                    key={`${uid}_dialog`}
                    header={__( 'YN.base.tree.couldNotSaveWhileErrors.title' )}
                    footer={<div><Button label="OK" className="p-button-outlined"
                                         onClick={() => this.props.setElusiveData( uid, { showCouldNotSavePopup: false } )}/></div>}
                    content=""
                    maskClassName="yn-dialog-panel-modal"
                    appendTo="self"
                    position="top"
                    visible={elusiveData?.showCouldNotSavePopup}
                    style={{ width: 'auto', maxWidth: '80%' }}
                    closable={false}
                    className="yn-error-dialog"
                    onHide={() => {
                    }}>
                    {errorMessage}
                </Dialog>
            }
            {!contentPanelOnly &&
                <Dialog
                    key={`${uid}_dataNotValid_dialog`}
                    header={__( 'YN.base.tree.dataNotValid.title' )}
                    content=""
                    footer={<div>
                        <Button
                            key="okButton"
                            label={__( 'YN.base.common.ok' )}
                            className="p-button p-button-outlined"
                            onClick={() => {
                                void this.props.closePanel( uid );
                            }}
                        />
                    </div>}
                    position="top"
                    maskClassName="yn-dialog-panel-modal"
                    appendTo="self"
                    visible={showDataNoLongerValidDialog}
                    closable={false}
                    className="yn-warning-dialog"
                    onHide={() => {
                        void this.props.closePanel( uid );
                    }}
                    style={{ width: 'auto', maxWidth: '80%' }}
                >
                    <p>{parse( __( 'YN.base.tree.dataNotValid.text' ) )}</p>
                </Dialog>
            }
            {!contentPanelOnly &&
                <TreeViewHeaderbar
                    key={`${uid}_TreeViewHeaderbar`}

                    uid={uid}

                    title={title}
                    icon={icon}
                />
            }
            {!contentPanelOnly &&
                <Toolbar
                    key={`${uid}_TreeViewToolbar`}

                    uid={uid}
                    schema={schema}
                    dataContext={dataContext}

                    rootItemId={rootItemId}

                    saveConfig={saveConfig}

                    saveButtonErrorCount={errorCount}
                    saveButtonErrorTooltip={errorTooltip}

                    onCopyXmlButtonClick={this.onCopyXmlButtonClick}
                    onDownloadXmlButtonClick={this.onDownloadXmlButtonClick}
                    onReloadButtonClick={this.onReloadButtonClick}

                    showModifiedHint={isDirty}

                    viewKey={viewKey}
                />
            }
            <LoadingMaskPanel forComponent={[ uid, uid + '_tree' ]} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    content=""
                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: '0' }}
                />
                <ResizeableLeftMenuPanel
                    key={`${uid}_menuContentPanel`}

                    uid={uid}

                    menuWidth={menuWidth}
                    minMenuWidth={15}
                    maxMenuWidth={85}
                    menu={
                        <ItemTree
                            ref={el => this.tree = el}
                            {...treeProps as ItemTreeProps}
                            filter
                            subPanelErrors={subPanelErrors}
                            dirtyNodeIds={dirtyNodeIds}
                            nodeIdToPanelKeyMap={nodeIdToPanelKeyMap}
                            className="yn-sidebar-content"
                            dataContext={dataContext}
                            uid={uid + '_tree'}
                            parentUid={uid}
                            schema={schema}
                            onGetTreeUtils={this.onGetTreeUtils}
                            onInvalidData={() => this.setState( { showDataNoLongerValidDialog: true } )}
                            onDataLoad={this.onTreeDataLoad}
                            onTreeDataChange={this.onTreeDataChange}
                            onTreeSelectionChange={this.onTreeSelectionChange}
                            onTreeToggleNode={this.onTreeToggleNode}
                            apiParams={{
                                viewKey,
                                rootItemId,
                                rootItemKey,
                                rootItemType,
                                // TODO: set this flag only if metaData is requested for xml export
                                includeMetaData: true,
                                ...additionalApiParams
                            }}
                        />
                    }

                    content={<LoadingMaskPanel
                        forComponent={panelConfig?.panelType === 'Form' || panelConfig?.panelType === 'Overview' ? `${uid}_${formUid}` : uid + '_attributes'}
                        className="yn-flex-layout-parent">
                        <div key={`${uid}_formview`} className="yn-content-panel-container yn-formview-panel"
                             style={{ flexFlow: 'row wrap', justifyContent: 'start' }}>
                            {item && panelConfig?.panelType === 'Form' &&
                                <Form
                                    {..._.pick( panelConfig, formComponentProps ) as FormComponentProps}

                                    key={`${uid}_${formUid}`}
                                    uid={`${uid}_${formUid}`}
                                    dataContext={dataContext}
                                    schema={schema}

                                    mode="embedded"

                                    {...formProps}

                                    rootItemId={item?.id}

                                    treeParentItemId={parentPayloadId}
                                    treeRootItemId={rootItemId}
                                    treeReloadData={this.onReloadButtonClick}
                                    treeVariables={{
                                        ...treeVariables,
                                        lastSaveTimestamp: elusiveData?.lastSaveTimestamp ?? 0
                                    }}
                                    treeSetVariables={this.onSetTreeVariables}
                                    treeDoSaveData={this.onSaveTreeData}

                                    onErrorDataChanged={this.onFormErrorDataChanged}
                                    onDirtyChanged={this.onFormDirtyChanged}

                                    ref={( el ) => this.form = el}
                                />
                            }
                            {panelConfig?.panelType === 'Overview' &&
                                <Overview
                                    {..._.pick( panelConfig, overviewComponentProps ) as OverviewComponentProps}

                                    key={`${uid}_${formUid}`}
                                    uid={`${uid}_${formUid}`}
                                    dataContext={dataContext}
                                    schema={schema}

                                    mode="embedded"

                                    {...formProps}

                                    additionalQueryParameters={{
                                        rootItemId      : item?.id ?? parentPayloadId ?? rootItemId,
                                        treeParentItemId: parentPayloadId ?? rootItemId,
                                        treeRootItemId  : rootItemId
                                    }}

                                    onErrorDataChanged={this.onFormErrorDataChanged}
                                />
                            }
                            {item && panelConfig?.panelType !== 'Form' && panelConfig?.panelType !== 'Overview' &&
                                <>
                                    <div style={{ display: 'flex', width: '100%', marginBottom: '1rem' }}>
                                        <DetailHeaderbar
                                            uid={`${uid}_formheader`}
                                            schema={schema}
                                            showSchema={false}
                                            showKey={false}
                                            showName={false}
                                            showStatus={false}
                                            item={item}
                                            style={{ flex: 1 }}
                                        />
                                    </div>
                                    <AttributesForm
                                        uid={`${uid}_${formUid}`}
                                        dataContext={dataContext}

                                        {...attributesPanelProps}

                                        schema={schema}
                                        status={status}
                                        timestamp={timestamp}
                                        timezone={timezone}

                                        item={item}

                                        rawMode={rawMode}
                                        showAdditionalRawNames={showAdditionalRawNames}

                                        onErrorDataChanged={this.onFormErrorDataChanged}

                                        editable
                                    />
                                </>
                            }
                        </div>
                    </LoadingMaskPanel>}
                />
            </LoadingMaskPanel>
        </>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {} as StateProps;

    // get intercom messages
    if ( state.intercom?.[ ownProps.uid ] ) {
        retVal.intercomMessages = state.intercom[ ownProps.uid ];
    }

    // get elusiveData
    if ( state.elusiveData?.[ ownProps.uid ] ) {
        retVal.elusiveData = state.elusiveData[ ownProps.uid ];
    }
    if ( state.elusiveData[ `${ownProps.uid}_tree` ] ) {
        retVal.treeVariables = state.elusiveData[ `${ownProps.uid}_tree` ]?.variables;
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( TreeView );
